/* eslint-disable no-console */
import { CommonModule } from '@angular/common';

import { Component, Inject, Input, signal, inject, effect, OnInit } from '@angular/core';
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { AccessionCardComponent } from '@/app/shared/cards/accession-card/accession-card.component';
import { PaginationComponent } from '@/app/shared/pagination/pagination.component';
import { SearchBarComponent } from '@/app/shared/search-bar/search-bar.component';
import { TabsComponent } from '@/app/shared/tabs/tabs.component';
import { MyAccessionsService } from '@/app/services/my-accessions.service';
import { FiltersComponent } from '@/app/shared/filters/filters.component';
import { filtersAdvanced } from '@/app/interfaces/filtersAdvanced';
import { filtersBasic } from '@/app/interfaces/filtersBasic';
import { DropdownComponent } from '@/app/shared/dropdown/dropdown.component';
import { ActivatedRoute, Router } from '@angular/router';
import { OrganismService } from '@/app/services/masters/organims.service';
import { AccessionsList, ApprovedActions } from '@/app/interfaces/accessionsList';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';

@Component({
  selector: 'app-my-accessions-list-sent',
  imports: [
    ConfirmDialog,
    AccessionCardComponent,
    CommonModule,
    TabsComponent,
    PaginationComponent,
    SearchBarComponent,
    ButtonsComponent,
    FiltersComponent,
    DropdownComponent,
  ],
  templateUrl: './my-accessions-list-sent.component.html',
  standalone: true,
  styleUrl: './my-accessions-list-sent.component.scss',
  providers: [ConfirmationService],
})
export class MyAccessionsListSentComponent implements OnInit {
  constructor(
    private confirmationService: ConfirmationService,
    private router: Router,
    @Inject(MyAccessionsService) private myAccessionsService: MyAccessionsService,
    @Inject(OrganismService) private organismService: OrganismService
  ) {}

  sentEffect = effect(() => {
    this.accessions.set([]);
    this.currentPage();
    this.paginationSize();
    this.loadAccessions();
  });

  @Input() ngModels;
  seachData = signal('');
  private route = inject(ActivatedRoute);

  paginationSize = signal(15);
  currentPage = signal(1);

  accessions = signal<AccessionsList[]>([]);
  totalResults = signal(0);

  jobCode = JSON.parse(localStorage.getItem('userData')).roleCode;
  tabsItems = signal([
    { title: 'Enviadas', url: '/mis-adhesiones/listado/enviadas', id: 'sent' },
    { title: 'Recibidas', url: '/mis-adhesiones/listado/recibidas', id: 'received' },
  ]);

  get filterParams() {
    return [
      {
        basicFilter: {
          nameOrderBy: this.orderByName(),
          orderBy: this.orderDirection(),
          currentPage: this.currentPage(),
          sizePages: this.paginationSize(),
          jobCode: this.jobCode,
        },
      },
      {
        advanceFilter: {
          state: this.advancedFilters.find((e) => e.label === 'Estados')?.selected,
          creationDateFrom: this.advancedFilters.find((e) => e.label === 'Fecha de creación')?.dateRangeOptions
            ?.startDate,
          creationDateTo: this.advancedFilters.find((e) => e.label === 'Fecha de creación')?.dateRangeOptions?.endDate,
          modifiedDateFrom: this.advancedFilters.find((e) => e.label === 'Fecha de modificación')?.dateRangeOptions
            ?.startDate,
          modifiedDateTo: this.advancedFilters.find((e) => e.label === 'Fecha de modificación')?.dateRangeOptions
            ?.endDate,
          organisms: this.advancedFilters.find((e) => e.label === 'Organismos')?.selected,
          search: this.seachData(),
        },
      },
    ];
  }

  organisms = [];

  advancedFilters: filtersAdvanced[] = [
    {
      type: 'multiselect',
      label: 'Organismo responsable',
      options: this.organisms,
      selected: [],
    },
    {
      type: 'multiselect',
      label: 'Estados',
      options: [
        { label: 'APROBADAS', value: 'APPROVED' },
        { label: 'RECHAZADAS', value: 'REJECTED' },
        { label: 'PENDIENTES', value: 'PENDING' },
        { label: 'BLOQUEADAS', value: 'BLOCKED' },
        { label: 'FINALIZADAS', value: 'FINISHED' },
      ],
      selected: [],
    },
    {
      type: 'dateRange',
      label: 'Fecha de creación',
      dateRangeOptions: {
        startDate: '',
        endDate: '',
      },
    },
    {
      type: 'dateRange',
      label: 'Fecha de modificación',
      dateRangeOptions: {
        startDate: '',
        endDate: '',
      },
    },
  ];
  orderby: filtersBasic = {
    type: 'dropdown',
    label: 'Ordenar adhesiones',
    classes: 'text-sm py-1',
    icon: 'pi pi-sort-alt',
    options: [
      { label: 'Ordenar por más reciente', value: 'recent' },
      { label: 'Ordenar por menos reciente', value: 'oldest' },
      { label: 'Ordenar por última modificación', value: 'update' },
    ],
    selected: '',
  };
  orderByName = signal('fecha_registro');
  orderDirection = signal('asc');
  navigateToEditRpts(id: string | number) {
    this.router.navigate(['/mis-adhesiones/detalles/editar-rpts/', id]);
  }

  onDropdownChange($event: string) {
    switch ($event) {
      case 'recent':
        {
          this.orderByName.set('fecha_registro');
          this.orderDirection.set('desc');
        }
        break;
      case 'oldest':
        {
          this.orderByName.set('fecha_registro');
          this.orderDirection.set('asc');
        }
        break;
      case 'update':
        {
          this.orderByName.set('fecha_ultima_modificación');
          this.orderDirection.set('desc');
        }
        break;
      default:
    }
  }

  handleApplyFilters(e) {
    this.advancedFilters = e;
    this.loadAccessions();
  }

  handleSearch(value: string) {
    this.seachData.set(value);
  }

  confirmUpdateAccession(accession, value) {
    this.confirmationService.confirm({
      message: 'Va a modificar el estado de una adhesión. ¿Está seguro/a de que desea continuar?',
      header: 'Confirmación',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Continuar',
      },
      accept: () => {
        this.handleActionAccesion(accession, value);
      },
    });
  }

  async handleActionAccesion(accesion, newValue) {
    if (accesion) {
      const params = {
        idAdhesion: accesion.id,
        state: newValue,
        modifiedDate: new Date(),
        deadline: null,
      };

      // obCode
      // idAdhesion
      // state
      // modifiedDate
      // y si se cambia de estado a finalizar tb hay que mandar fecha_limite
      if (newValue === 'finished') {
        params.deadline = new Date();
      } else {
        delete params.deadline;
      }
      const response = await this.myAccessionsService.updateAccesion(params);
      if (response) {
        this.loadAccessions();
      }
    }
  }

  getValidatedTime(accession: AccessionsList): Date {
    return accession.validatedTime ? new Date(accession.validatedTime) : this.getCreateDate(accession);
  }

  getCreateDate(accession: AccessionsList): Date {
    return new Date(accession.creationDate);
  }

  getAccessionId(accession: AccessionsList): string {
    return String(accession.id);
  }

  getApprovedActions(accession: AccessionsList): ApprovedActions {
    if (accession.approvedActions?.options?.length) {
      return accession.approvedActions;
    }
    return null;
  }

  private async loadAccessions() {
    const response = (await this.myAccessionsService.getAllAccessionsSent(this.filterParams)) as {
      data: { items: AccessionsList[]; total: number };
      message: string;
    };
    if (response?.message !== 'OK') {
      return;
    } else {
      response.data.items.forEach((e) => {
        e.approvedActions = {
          label: 'Más',
          options: [],
        };

        const options = [];

        if (e.state.toLocaleLowerCase() !== 'finished') {
          options.push({
            label: 'Modificar rpts',
            value: 'Modificar rpts',
            action: () => this.navigateToEditRpts(e.id),
          });
        }

        if (e.state.toLocaleLowerCase() === 'pending') {
          options.push({
            label: 'Finalizar',
            value: 'finished',
            action: () => this.confirmUpdateAccession(e, 'finished'),
          });
        }

        if (options.length === 1) {
          e.action = options[0].action;
        } else if (options.length > 1) {
          e.approvedActions.options = options;
        }
      });
      this.accessions.set(response.data.items);
      this.totalResults.set(response.data.total);
    }
  }

  async ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      if (params?.['state']) {
        const stateValue = params['state']?.toLowerCase();
        let mappedState: string;

        switch (stateValue) {
          case 'pending':
            mappedState = 'PENDING';
            break;
          case 'approved':
            mappedState = 'APPROVED';
            break;
          case 'rejected':
            mappedState = 'REJECTED';
            break;
          case 'finished':
            mappedState = 'FINISHED';
            break;
          case 'blocked':
            mappedState = 'BLOCKED';
            break;
          default:
            mappedState = params['state'].toUpperCase();
        }

        const stateFilter = this.advancedFilters.find((e) => e.label === 'Estados');

        if (stateFilter) {
          stateFilter.selected = [mappedState];

          if (stateFilter.options) {
            stateFilter.options.forEach((option) => (option.check = false));
            const targetOption = stateFilter.options.find((option) => option.value === mappedState);
            if (targetOption) {
              targetOption.check = true;
            }
          }
        }
      }
    });

    // Load organisms for the filter
    const response = await this.organismService.getOrganism();
    if (response?.data?.items.length) {
      response.data.items.forEach((e) => {
        this.organisms.push({
          label: e.nombre_organismo,
          value: e.codigo_organismo,
          check: false,
        });
      });
    }
  }
}
